import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  getSkillPathError,
  MAX_SKILL_PDF_BYTES,
  MAX_SKILL_TEXT_FILE_BYTES,
  MimeType,
  parseSkillTextContentType,
  SKILL_PATH_KINDS,
} from '@aline/common';
import type { SkillTextContentType } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { Alert, DialogContent, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { saveAs } from 'file-saver';
import { Download, FilePlus, FolderOpen, RefreshCw, Upload, X } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineTooltip } from 'src/components';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import { AlineDialogButtons } from 'src/components/generic/AlineDialogButtons';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { DocumentPicker, DocumentPickerVariant } from 'src/components/generic/DocumentPicker';
import type { DocumentPickerRow } from 'src/components/generic/DocumentPicker';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SkillHeaderSlot } from './skill-file-header';
import { getAttachmentLimitError, getIsSkillPathTaken } from './skill-file-rules';
import { getSkillDocumentItems } from './skill-file-tree';
import { SkillRemoveButton } from './skill-remove-button';
import { SkillResourcePreview } from './skill-resource-preview';
import type { SkillResourcePreviewProps } from './skill-resource-preview';
import { getParentPath, joinSkillPath } from './skill-tree-relocation';
import {
  decodeSkillAttachmentBytes,
  fetchSkillAttachmentContent,
} from './use-skill-attachment-content';

export type SkillAttachmentChanges = Pick<
  Apollo.UpdateAgentSkillInput,
  'retainedAttachmentPaths' | 'textFiles' | 'attachmentRenames'
> & {
  /** Documents from My documents. The save copies each and names it after its document. */
  documentSnapshots?: Apollo.SkillDocumentSnapshotInput[];
  pdfAttachments?: (Apollo.SkillPdfAttachmentInput & { id: string })[];
};

type Attachment = Apollo.AgentSkillSimpleFragment['attachments'][number];
type PdfAttachment = NonNullable<SkillAttachmentChanges['pdfAttachments']>[number];
type DocumentSnapshot = NonNullable<SkillAttachmentChanges['documentSnapshots']>[number];
type TextFile = NonNullable<SkillAttachmentChanges['textFiles']>[number];

/** A file picked on this device, and the folder it goes in. */
interface PendingUpload {
  file: File;
  folderPath: string;
}

type UploadContentType = SkillTextContentType | typeof MimeType.PDF;

const COPY_CHANGED_MESSAGE = 'A newer document version is available.';
const UPLOAD_ACCEPT = 'application/pdf,text/markdown,text/plain,application/json,.md,.txt,.json';
/** Types some browsers report for Markdown instead of `text/markdown`. */
const MARKDOWN_TYPE_ALIASES = ['text/x-markdown'];
/**
 * Only for a file the browser gave no type, which happens to Markdown on some systems. The
 * server still checks a PDF's bytes.
 */
const UPLOAD_TYPES_BY_EXTENSION: Record<string, UploadContentType> = {
  json: 'application/json',
  markdown: 'text/markdown',
  md: 'text/markdown',
  pdf: MimeType.PDF,
  txt: 'text/plain',
};

const useStyles = makeStyles()(theme => ({
  // A full-width bar under the file header, like the skill's publish notice.
  copyNotice: {
    '& .MuiAlert-action': { alignItems: 'center', gap: 4, marginRight: 0, paddingTop: 0 },
    '& .MuiAlert-icon': { padding: 0 },
    '& .MuiAlert-message': { flex: 1, minWidth: 0, padding: 0 },
    alignItems: 'center',
    borderRadius: 0,
    fontSize: 12,
    minHeight: 36,
    padding: theme.spacing(0, 2),
  },
  copyNoticeText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pickerButtons: { flexShrink: 0 },
  pickerContent: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
  },
  pickerPaper: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    maxHeight: 'calc(100vh - 64px)',
    maxWidth: 1080,
    minWidth: '75%',
  },
  root: { display: 'flex', flexDirection: 'column' },
}));

/**
 * The "Add file" button and its menu, for the file tree header.
 *
 * The button stays enabled while a save runs, so autosave does not make it blink: a file
 * added meanwhile joins the next save. The file input lives outside the menu, so a file the
 * author picks still arrives if the menu closes first.
 */
const SkillAddFileMenu = React.memo(function SkillAddFileMenu({
  editable,
  onFileChosen,
  onMyDocuments,
  onNewFile,
}: {
  editable: boolean;
  onFileChosen: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onMyDocuments: () => void;
  onNewFile?: () => void;
}) {
  const menuId = useId();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!editable) setMenuAnchor(null);
  }, [editable]);
  const openMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget),
    []
  );
  const closeMenu = useCallback(() => setMenuAnchor(null), []);
  const handleNewFile = useCallback(() => {
    setMenuAnchor(null);
    onNewFile?.();
  }, [onNewFile]);
  const handleUpload = useCallback(() => {
    setMenuAnchor(null);
    inputRef.current?.click();
  }, []);
  const handleMyDocuments = useCallback(() => {
    setMenuAnchor(null);
    onMyDocuments();
  }, [onMyDocuments]);
  return (
    <>
      <AlineTooltip arrow placement="top" title="Add file">
        <span>
          <IconButton
            aria-controls={menuAnchor ? menuId : undefined}
            aria-expanded={Boolean(menuAnchor)}
            aria-haspopup="menu"
            aria-label="Add file"
            disabled={!editable}
            size="small"
            sx={{
              height: 28,
              width: 28,
              ...(menuAnchor ? { backgroundColor: 'action.selected', color: 'primary.main' } : {}),
            }}
            onClick={openMenu}
          >
            <FilePlus size={16} />
          </IconButton>
        </span>
      </AlineTooltip>
      <input
        ref={inputRef}
        hidden
        accept={UPLOAD_ACCEPT}
        data-testid="skill-file-upload"
        type="file"
        onChange={onFileChosen}
      />
      <Menu anchorEl={menuAnchor} id={menuId} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={handleNewFile}>
          <ListItemIcon>
            <FilePlus size={18} />
          </ListItemIcon>
          New file
        </MenuItem>
        <MenuItem onClick={handleUpload}>
          <ListItemIcon>
            <Upload size={18} />
          </ListItemIcon>
          Upload
        </MenuItem>
        <MenuItem onClick={handleMyDocuments}>
          <ListItemIcon>
            <FolderOpen size={18} />
          </ListItemIcon>
          My documents
        </MenuItem>
      </Menu>
    </>
  );
});

/**
 * A dismissable bar under the file header once a copy's source document has a newer
 * version. Dismissing hides it for this copy; a replaced copy can show it again.
 */
const SkillCopyNotice = React.memo(function SkillCopyNotice({
  canUpdate,
  file,
  onUpdateCopy,
}: {
  canUpdate: boolean;
  file: Attachment;
  onUpdateCopy: (file: Attachment) => void;
}) {
  const { classes } = useStyles();
  const copyKey = `${file.path}:${file.sha256}`;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const dismiss = useCallback(() => setDismissedKey(copyKey), [copyKey]);
  const updateCopy = useCallback(() => onUpdateCopy(file), [file, onUpdateCopy]);
  if (file.sourceStatus !== Apollo.AgentSkillAttachmentSourceStatus.Changed) return null;
  if (dismissedKey === copyKey) return null;

  return (
    <Alert
      action={
        <>
          {canUpdate && (
            <AlineButton
              color="primary"
              size="small"
              startIcon={<RefreshCw size={14} />}
              onClick={updateCopy}
            >
              Update copy
            </AlineButton>
          )}
          <IconButton aria-label="Dismiss" size="small" onClick={dismiss}>
            <X size={16} />
          </IconButton>
        </>
      }
      className={classes.copyNotice}
      severity="info"
    >
      <span className={classes.copyNoticeText} title={COPY_CHANGED_MESSAGE}>
        {COPY_CHANGED_MESSAGE}
      </span>
    </Alert>
  );
});

/** A file saved in the current version: its header actions and how its copy stands. */
const SavedAttachmentActions = React.memo(function SavedAttachmentActions({
  editable,
  file,
  headerActionsContainer,
  onDownload,
  onUpdateCopy,
}: {
  editable: boolean;
  file: Attachment;
  onDownload: (file: Attachment) => Promise<void>;
  onUpdateCopy: (file: Attachment) => void;
  headerActionsContainer?: HTMLElement | null;
}) {
  const download = useCallback(() => void onDownload(file), [file, onDownload]);
  return (
    <>
      <SkillHeaderSlot container={headerActionsContainer}>
        <AlineTooltip arrow placement="top" title="Download copy">
          <IconButton aria-label="Download copy" size="small" onClick={download}>
            <Download size={16} />
          </IconButton>
        </AlineTooltip>
      </SkillHeaderSlot>
      <SkillCopyNotice
        canUpdate={editable && Boolean(file.sourceDocumentId)}
        file={file}
        onUpdateCopy={onUpdateCopy}
      />
    </>
  );
});

/** A PDF picked on this device, or a document copy, not saved yet. */
const PendingFileActions = React.memo(function PendingFileActions({
  editable,
  headerActionsContainer,
  id,
  name,
  onRemove,
}: {
  editable: boolean;
  headerActionsContainer: HTMLElement | null | undefined;
  /** The PDF's id, or the copied document's id. */
  id: string;
  name: string;
  onRemove: (id: string) => void;
}) {
  const remove = useCallback(() => onRemove(id), [id, onRemove]);
  return (
    <SkillHeaderSlot container={headerActionsContainer}>
      {editable && <SkillRemoveButton name={name} onConfirm={remove} />}
    </SkillHeaderSlot>
  );
});

/**
 * Adds files to a skill and previews the one selected in the file tree.
 *
 * With `actionsOnly` it renders just the "Add file" menu; otherwise it previews
 * `selectedItem`: `saved:<path>` for a saved file, `pdf:<id>` for a PDF picked on this device
 * and `snapshot:<documentId>` for a document copy that is not saved yet.
 *
 * `onChange` takes an updater, and every change is built from the latest changes rather than
 * the ones this component rendered with. Reading a file or capturing a document takes a
 * while, and a save that lands meanwhile must not be undone by writing back older changes.
 */
export const SkillAttachmentEditor = React.memo(function SkillAttachmentEditor({
  actionsOnly = false,
  changes,
  editable,
  folderPath = '',
  folders = [],
  headerActionsContainer,
  onChange,
  onNewFile,
  onReplaceCopy,
  previewControlsContainer,
  saving = false,
  selectedItem = '',
  skill,
}: {
  changes: SkillAttachmentChanges;
  /** Whether the author can change files. */
  editable: boolean;
  onChange: React.Dispatch<React.SetStateAction<SkillAttachmentChanges>>;
  actionsOnly?: boolean;
  /** Folder new files are added to, '' for the skill root. */
  folderPath?: string;
  folders?: string[];
  headerActionsContainer?: HTMLElement | null;
  onNewFile?: () => void;
  /**
   * Called with the document whose saved copy "Update copy" just replaced, so the page can
   * keep the new copy selected instead of falling back to SKILL.md.
   */
  onReplaceCopy?: (documentId: string) => void;
  previewControlsContainer?: HTMLElement | null;
  /**
   * Whether a save is running. A file not saved yet cannot be removed meanwhile: the save is
   * storing it, and it would come back once the save lands.
   */
  saving?: boolean;
  selectedItem?: string;
  skill?: Apollo.AgentSkillSimpleFragment | null;
}) {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const latestRef = useRef({ folders, skill });
  latestRef.current = { folders, skill };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [replacement, setReplacement] = useState<Attachment | null>(null);
  const apolloClient = useApolloClient();
  const retainedPaths = useMemo(() => getRetainedPaths({ changes, skill }), [changes, skill]);

  const showError = useCallback(
    (error: unknown) => {
      enqueueSnackbar(error instanceof Error ? error.message : 'Could not load the attachment.', {
        variant: 'error',
      });
    },
    [enqueueSnackbar]
  );

  const getIsRowDisabled = useCallback(
    (row: DocumentPickerRow) => {
      if (
        skill?.attachments.some(
          file => retainedPaths.includes(file.path) && file.sourceDocumentId === row.id
        ) ||
        changes.documentSnapshots?.some(file => file.documentId === row.id)
      )
        return { isRowDisabled: true, reason: 'Already attached to this skill.' };
      // Uploaded files other than PDFs (Word files, images) have no text a skill can read.
      if (
        row.latestVersion.__typename === 'StaticFileVersion' &&
        row.latestVersion.fileLocation.mimetype !== MimeType.PDF
      )
        return { isRowDisabled: true, reason: 'Only Aline documents and PDFs can be attached.' };
      if (selectedDocumentIds.includes(row.id)) return { isRowDisabled: false, reason: '' };
      const limitError = getAttachmentLimitError({
        adding: selectedDocumentIds.length + 1,
        pending: getPendingCount(changes),
        retained: retainedPaths.length,
      });
      if (limitError) return { isRowDisabled: true, reason: limitError };
      return { isRowDisabled: false, reason: '' };
    },
    [changes, retainedPaths, selectedDocumentIds, skill?.attachments]
  );

  const handleOpenPicker = useCallback(() => {
    setSelectedDocumentIds([]);
    setReplacement(null);
    setPickerOpen(true);
  }, []);

  const handleUpdateCopy = useCallback((file: Attachment) => {
    setReplacement(file);
    setSelectedDocumentIds([]);
    setPickerOpen(true);
  }, []);

  const handleClosePicker = useCallback(() => setPickerOpen(false), []);

  /**
   * Adds the picked documents to the changes. The next autosave copies each one (an Aline
   * document as a DOCX, a PDF as its bytes) into the draft, and the preview shows that copy.
   */
  const handleAddDocuments = useCallback(() => {
    const ids = replacement?.sourceDocumentId
      ? [replacement.sourceDocumentId]
      : selectedDocumentIds;
    // A refreshed copy stays where the old one was; a new document goes to the chosen folder.
    const targetFolder = replacement ? getParentPath(replacement.path) : folderPath;
    setPickerOpen(false);
    if (!ids.length) return false;
    const snapshots: DocumentSnapshot[] = ids.map(documentId => ({
      documentId,
      folderPath: targetFolder,
    }));
    onChange(current => {
      const retained = getRetainedPaths({ changes: current, skill: latestRef.current.skill });
      return {
        ...current,
        documentSnapshots: [
          ...(current.documentSnapshots ?? []).filter(
            file => !snapshots.some(snapshot => snapshot.documentId === file.documentId)
          ),
          ...snapshots,
        ],
        ...(replacement
          ? { retainedAttachmentPaths: retained.filter(path => path !== replacement.path) }
          : {}),
      };
    });
    if (replacement?.sourceDocumentId) onReplaceCopy?.(replacement.sourceDocumentId);
    return true;
  }, [folderPath, onChange, onReplaceCopy, replacement, selectedDocumentIds]);

  const addUpload = useCallback(
    async (upload: PendingUpload) => {
      const { file } = upload;
      try {
        const contentType = getUploadContentType(file);
        if (contentType && contentType !== MimeType.PDF) {
          const content = await file.text();
          const path = getUploadPath(upload);
          onChange(current => {
            const error = getUploadError({ changes: current, ...latestRef.current, upload });
            if (error) throw new Error(error);
            return {
              ...current,
              textFiles: [...(current.textFiles ?? []), { content, contentType, path }],
            };
          });
          return;
        }
        const base64 = await readFileAsBase64(file);
        const id = crypto.randomUUID();
        onChange(current => ({
          ...current,
          pdfAttachments: [
            ...(current.pdfAttachments ?? []),
            { base64, folderPath: upload.folderPath, id, name: file.name },
          ],
        }));
      } catch (error) {
        showError(error);
      }
    },
    [onChange, showError]
  );

  const handleFileChosen = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      const upload = { file, folderPath };
      const error =
        getUploadError({ changes, folders, skill, upload }) ??
        getAttachmentLimitError({
          adding: 1,
          pending: getPendingCount(changes),
          retained: retainedPaths.length,
        });
      if (error) {
        showError(new Error(error));
        return;
      }
      void addUpload(upload);
    },
    [addUpload, changes, folderPath, folders, retainedPaths.length, showError, skill]
  );

  const removePdf = useCallback(
    (id: string) =>
      onChange(current => ({
        ...current,
        pdfAttachments: current.pdfAttachments?.filter(item => item.id !== id),
      })),
    [onChange]
  );

  const removeSnapshot = useCallback(
    (documentId: string) =>
      onChange(current => ({
        ...current,
        documentSnapshots: current.documentSnapshots?.filter(
          item => item.documentId !== documentId
        ),
      })),
    [onChange]
  );

  const download = useCallback(
    async (attachment: Attachment) => {
      if (!skill) return;
      try {
        const content = await fetchSkillAttachmentContent({
          client: apolloClient,
          path: attachment.path,
          skill,
        });
        saveAs(
          new Blob([decodeSkillAttachmentBytes(content.base64)], { type: content.contentType }),
          content.name
        );
      } catch (error) {
        showError(error);
      }
    },
    [apolloClient, showError, skill]
  );

  const shownItem = actionsOnly ? '' : selectedItem;
  const savedFile = skill?.attachments.find(
    file => retainedPaths.includes(file.path) && shownItem === `saved:${file.path}`
  );
  const pendingPdf = changes.pdfAttachments?.find(file => shownItem === `pdf:${file.id}`);
  const pendingSnapshot = changes.documentSnapshots?.find(
    file => shownItem === `snapshot:${file.documentId}`
  );
  const pendingTextFile = changes.textFiles?.find(file => shownItem === `text:${file.path}`);
  const resourcePreview = getResourcePreview({
    pendingPdf,
    pendingSnapshot,
    pendingTextFile,
    savedFile,
    skill,
  });

  return (
    <div className={classes.root}>
      {actionsOnly && (
        <SkillAddFileMenu
          editable={editable}
          onFileChosen={handleFileChosen}
          onMyDocuments={handleOpenPicker}
          onNewFile={onNewFile}
        />
      )}
      {savedFile && skill && (
        <SavedAttachmentActions
          editable={editable}
          file={savedFile}
          headerActionsContainer={headerActionsContainer}
          onDownload={download}
          onUpdateCopy={handleUpdateCopy}
        />
      )}
      {pendingPdf && (
        <PendingFileActions
          editable={editable && !saving}
          headerActionsContainer={headerActionsContainer}
          id={pendingPdf.id}
          name={pendingPdf.name}
          onRemove={removePdf}
        />
      )}
      {resourcePreview && (
        <SkillResourcePreview {...resourcePreview} controlsContainer={previewControlsContainer} />
      )}
      {pendingSnapshot && (
        <PendingFileActions
          editable={editable && !saving}
          headerActionsContainer={headerActionsContainer}
          id={pendingSnapshot.documentId}
          name={pendingSnapshot.name ?? 'this document'}
          onRemove={removeSnapshot}
        />
      )}
      <AlineDialog
        classes={{ paperWidthMd: classes.pickerPaper }}
        open={pickerOpen && !replacement}
        title="My documents"
        onClose={handleClosePicker}
      >
        <DialogContent className={classes.pickerContent}>
          {currentUser?.myRepositoryId && (
            <DocumentPicker
              showFiltersButton
              getIsRowDisabled={getIsRowDisabled}
              pickerVariant={DocumentPickerVariant.MultiSelect}
              repositoryId={currentUser.myRepositoryId}
              onSelectionChange={setSelectedDocumentIds}
            />
          )}
        </DialogContent>
        <AlineDialogButtons
          cancelHandler={handleClosePicker}
          className={classes.pickerButtons}
          disabled={!selectedDocumentIds.length}
          submitHandler={handleAddDocuments}
          submitText={getPickerSubmitText(selectedDocumentIds.length)}
        />
      </AlineDialog>
      <ConfirmationDialog
        message={`Replace this copy with the latest version of '${replacement?.name ?? ''}'? Anyone with access to this skill can read the updated copy.`}
        open={pickerOpen && Boolean(replacement)}
        primaryText="Update copy"
        secondaryText="Cancel"
        title="Update document copy"
        variant={ConfirmationDialogVariant.Info}
        onClickPrimary={handleAddDocuments}
        onClose={handleClosePicker}
      />
    </div>
  );
});

/** The saved files this save keeps: the ones the author has not removed. */
function getRetainedPaths({
  changes,
  skill,
}: {
  changes: SkillAttachmentChanges;
  skill: Apollo.AgentSkillSimpleFragment | null | undefined;
}): string[] {
  return changes.retainedAttachmentPaths ?? skill?.attachments.map(file => file.path) ?? [];
}

/** How many files the changes add: copies, PDFs and text files not saved yet. */
function getPendingCount(changes: SkillAttachmentChanges): number {
  return (
    (changes.documentSnapshots?.length ?? 0) +
    (changes.pdfAttachments?.length ?? 0) +
    (changes.textFiles?.length ?? 0)
  );
}

/**
 * What the selected file previews: a picked PDF, a picked file the editor does not open (JSON),
 * a saved file, or a copy still saving.
 */
function getResourcePreview({
  pendingPdf,
  pendingSnapshot,
  pendingTextFile,
  savedFile,
  skill,
}: {
  pendingPdf: PdfAttachment | undefined;
  pendingSnapshot: DocumentSnapshot | undefined;
  pendingTextFile: TextFile | undefined;
  savedFile: Attachment | undefined;
  skill: Apollo.AgentSkillSimpleFragment | null | undefined;
}): SkillResourcePreviewProps | null {
  if (pendingPdf) return { base64: pendingPdf.base64, contentType: MimeType.PDF, kind: 'upload' };
  if (pendingTextFile) return { kind: 'text', text: pendingTextFile.content };
  if (savedFile && skill)
    return {
      kind: 'saved',
      path: savedFile.path,
      skill,
      snapshotDocumentId: savedFile.sourceDocumentId ?? undefined,
    };
  if (pendingSnapshot)
    return {
      kind: 'text',
      snapshotDocumentId: pendingSnapshot.documentId,
      text: 'Saving the copy…',
    };
  return null;
}

/** Why a picked file cannot be uploaded, or `null` when it can. */
function getUploadError({
  changes,
  folders,
  skill,
  upload,
}: {
  changes: SkillAttachmentChanges;
  folders: string[];
  skill: Apollo.AgentSkillSimpleFragment | null | undefined;
  upload: PendingUpload;
}): string | null {
  const { file } = upload;
  const contentType = getUploadContentType(file);
  if (!contentType) return 'Upload a PDF, Markdown, text or JSON file.';
  if (contentType !== MimeType.PDF) {
    // The server refuses anything larger, so say so before reading the file.
    if (file.size > MAX_SKILL_TEXT_FILE_BYTES)
      return 'Upload a Markdown, text or JSON file no larger than 1 MB.';
    const path = getUploadPath(upload);
    const pathError = getSkillPathError(path, SKILL_PATH_KINDS.file);
    if (pathError) return pathError;
    if (
      getIsSkillPathTaken({
        documentItems: getSkillDocumentItems({ attachments: skill?.attachments ?? [], changes }),
        folders,
        path,
        self: null,
      })
    )
      return 'A file with this name already exists.';
    return null;
  }
  if (file.size > MAX_SKILL_PDF_BYTES) return 'Upload a PDF no larger than 10 MB.';
  return null;
}

/**
 * What a picked file is, from the type the browser reports, or null when it is not a PDF,
 * Markdown, text or JSON file. The name is read only when the browser reports no type.
 */
function getUploadContentType(file: Pick<File, 'name' | 'type'>): UploadContentType | null {
  const mediaType = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  if (!mediaType) {
    const extension = file.name.includes('.') ? file.name.split('.').at(-1)?.toLowerCase() : '';
    return UPLOAD_TYPES_BY_EXTENSION[extension ?? ''] ?? null;
  }
  if (mediaType === MimeType.PDF) return MimeType.PDF;
  if (MARKDOWN_TYPE_ALIASES.includes(mediaType)) return 'text/markdown';
  return parseSkillTextContentType(mediaType);
}

function getUploadPath({ file, folderPath }: PendingUpload): string {
  return joinSkillPath(folderPath, file.name);
}

function getPickerSubmitText(selectedCount: number): string {
  if (!selectedCount) return 'Add documents';
  return `Add ${selectedCount} document${selectedCount === 1 ? '' : 's'}`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result.split(',')[1] ?? '')
        : reject(new Error('Could not read this file.'));
    reader.onerror = () => reject(new Error('Could not read this file.'));
    reader.readAsDataURL(file);
  });
}
